import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { isAdminUser } from "../data/adminUsers";
import { getFinalPositions, getFinalPositionsPublic } from "../data";
import { ShipPosition } from "../types/ShipPosition";

type SortColumn = "SHIPNAME" | "LINE" | "SPEED" | "FUEL_CONSUMPTION" | "HOURLY_FUEL_COST" | "ANNUAL_FUEL_BILL" | "NEXT_PORT_NAME";
type SortDir = "asc" | "desc";

const GATED_COLUMNS: SortColumn[] = ["SPEED", "FUEL_CONSUMPTION", "HOURLY_FUEL_COST", "ANNUAL_FUEL_BILL"];

const MEDAL = ["🥇", "🥈", "🥉"];

export default function Fleet() {
  const [ships, setShips] = useState<ShipPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("ANNUAL_FUEL_BILL");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsSince, setSecondsSince] = useState(0);

  const { isSignedIn, isLoaded, getToken, has, userId } = useAuth();
  const isCaptain = isLoaded && (has({ plan: "vessel_vibes_captain" }) || isAdminUser(userId));
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoaded) return;
    const load = async () => {
      setLoading(true);
      if (isSignedIn && isCaptain) {
        const token = await getToken();
        if (!token) return;
        const data = await getFinalPositions(token);
        setShips(data);
      } else {
        const data = await getFinalPositionsPublic();
        setShips(data);
      }
      setLastUpdated(new Date());
      setSecondsSince(0);
      setLoading(false);
    };
    load();
    const refresh = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(refresh);
  }, [isLoaded, isSignedIn, isCaptain, getToken]);

  // Tick the "last updated" counter
  useEffect(() => {
    if (!lastUpdated) return;
    const tick = setInterval(() => {
      setSecondsSince(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortColumn(col);
      setSortDir("desc");
    }
  };

  const filtered = ships
    .filter((ship) => {
      const term = searchTerm.toLowerCase();
      return (
        ship.SHIPNAME?.toLowerCase().includes(term) ||
        ship.LINE?.toLowerCase().includes(term) ||
        ship.NEXT_PORT_NAME?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      const aVal = a[sortColumn] ?? "";
      const bVal = b[sortColumn] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "desc" ? bVal - aVal : aVal - bVal;
      }
      return sortDir === "desc"
        ? String(bVal).localeCompare(String(aVal))
        : String(aVal).localeCompare(String(bVal));
    });

  const handleRowClick = (ship: ShipPosition) => {
    navigate("/", {
      state: {
        focusShip: {
          lat: Number(ship.LAT),
          lon: Number(ship.LON),
          name: ship.SHIPNAME,
        },
      },
    });
  };

  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortColumn !== col) return <span className="ml-1 text-gray-600">↕</span>;
    return <span className="ml-1 text-blue-400">{sortDir === "desc" ? "↓" : "↑"}</span>;
  };

  const thClass = "px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none";
  const tdClass = "px-4 py-3 text-xs font-mono";

  return (
    <div className="min-h-screen bg-gray-950 text-white pt-16">
      {/* Header */}
      <div className="border-b border-gray-800 px-6 py-5">
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white">
              Fleet Intelligence
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Live data for {ships.length} cruise ships worldwide
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              {lastUpdated
                ? secondsSince < 60
                  ? `Updated ${secondsSince}s ago`
                  : `Updated ${Math.floor(secondsSince / 60)}m ago`
                : "Loading..."}
            </div>
            {isCaptain && (
              <p className="text-[11px] text-gray-600 italic">
                Fuel figures are algorithmic estimates — for informational purposes only
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-4">
        {/* Search */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by ship, cruise line, or port..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-xs text-gray-500 self-center">
            {filtered.length} ships
          </span>
        </div>

        {/* Auth banner for locked columns */}
        {!isCaptain && (
          <div className="mb-4 flex items-center justify-between bg-blue-950/50 border border-blue-800/50 rounded-lg px-4 py-2.5">
            <p className="text-xs text-blue-300">
              🔒 Speed, fuel burn, fuel cost, and annual fuel bill are locked — visible with a Captain subscription
            </p>
            <a href="/captain" className="ml-4 shrink-0 px-3 py-1 bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-semibold rounded transition-colors">
              Get Captain — $4.99/mo
            </a>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
            Loading fleet data...
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-gray-800">
            <table className="w-full border-collapse">
              <thead className="bg-gray-900 sticky top-0 z-10">
                <tr>
                  <th className={thClass} onClick={() => handleSort("SHIPNAME")}>
                    Ship <SortIcon col="SHIPNAME" />
                  </th>
                  <th className={thClass} onClick={() => handleSort("LINE")}>
                    Cruise Line <SortIcon col="LINE" />
                  </th>
                  <th className={thClass} onClick={() => handleSort("SPEED")}>
                    Speed <SortIcon col="SPEED" />
                  </th>
                  <th className={thClass} onClick={() => handleSort("FUEL_CONSUMPTION")}>
                    Fuel Burn <SortIcon col="FUEL_CONSUMPTION" />
                  </th>
                  <th className={thClass} onClick={() => handleSort("HOURLY_FUEL_COST")}>
                    Fuel Cost/hr <SortIcon col="HOURLY_FUEL_COST" />
                  </th>
                  <th className={thClass} onClick={() => handleSort("ANNUAL_FUEL_BILL")}>
                    Annual Fuel Bill <SortIcon col="ANNUAL_FUEL_BILL" />
                  </th>
                  <th className={thClass} onClick={() => handleSort("NEXT_PORT_NAME")}>
                    Next Port <SortIcon col="NEXT_PORT_NAME" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ship, i) => {
                  const medal = i < 3 && GATED_COLUMNS.includes(sortColumn) && isCaptain ? MEDAL[i] : null;
                  const isTop = i < 3 && GATED_COLUMNS.includes(sortColumn) && isCaptain;
                  return (
                    <tr
                      key={`${ship.SHIPNAME}-${i}`}
                      onClick={() => handleRowClick(ship)}
                      className={`border-t border-gray-800/60 cursor-pointer transition-colors ${
                        isTop
                          ? "bg-blue-950/20 hover:bg-blue-950/40"
                          : "hover:bg-gray-900/60"
                      }`}
                    >
                      <td className={`${tdClass} font-semibold text-white`}>
                        {medal && <span className="mr-1">{medal}</span>}
                        {ship.SHIPNAME}
                      </td>
                      <td className={`${tdClass} text-gray-400`}>{ship.LINE || "—"}</td>

                      {/* Gated: Speed */}
                      <td className={tdClass}>
                        {isCaptain ? (
                          <span className="text-green-400">{ship.SPEED} kn</span>
                        ) : (
                          <span className="inline-block w-12 h-2.5 bg-gray-700 rounded blur-[2px]" />
                        )}
                      </td>

                      {/* Gated: Fuel Burn */}
                      <td className={tdClass}>
                        {isCaptain ? (
                          <span className="text-green-400">{Number(ship.FUEL_CONSUMPTION).toFixed(2)} MT/hr</span>
                        ) : (
                          <span className="inline-block w-16 h-2.5 bg-gray-700 rounded blur-[2px]" />
                        )}
                      </td>

                      {/* Gated: Fuel Cost */}
                      <td className={tdClass}>
                        {isCaptain ? (
                          <span className="text-green-400">${Number(ship.HOURLY_FUEL_COST ?? 0).toFixed(0)}/hr</span>
                        ) : (
                          <span className="inline-block w-16 h-2.5 bg-gray-700 rounded blur-[2px]" />
                        )}
                      </td>

                      {/* Gated: Annual Fuel Bill */}
                      <td className={tdClass}>
                        {isCaptain ? (
                          <span className="text-yellow-400 font-semibold">${Number(ship.ANNUAL_FUEL_BILL ?? 0).toLocaleString()}</span>
                        ) : (
                          <span className="inline-block w-20 h-2.5 bg-gray-700 rounded blur-[2px]" />
                        )}
                      </td>

                      <td className={`${tdClass} text-gray-300`}>{ship.NEXT_PORT_NAME || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
